/* Product information (clean): price summary, per-cup price, selling plan picker and share.
   (2026-09-22: re-pushed so the Shopify GitHub sync, which missed one push, picks this file up.)
   Variant switching itself is product.js (variant-selects / product-form); this listens to it.
   When the section places a subscription app block in Step 2 ([data-app-plans]), the app owns
   the selling plan: Appstle's widget adds selling_plan to /cart/add itself. This only reads the
   chosen plan back (its sellingPlanChanged event, plus change events inside the widget) so the
   price summary and per-cup price follow the shopper's choice. */
(function () {
  if (customElements.get('clean-buybox')) return;

  /* ---- Appstle leak guard (2026-10-01) ----
     Appstle's product-page widget rebuilds itself about three times on every variant change
     (product.js dispatches change on the id input; Appstle also watches that input and the URL)
     and each rebuild leaves two things behind: its three <style> blocks are appended to <head>
     again (one of them ~45 KB of CSS, never removed), and its delegated radio handlers are bound
     on document again without the previous ones being unbound. After thirty size switches the
     page carried ~1 MB of duplicate CSS and each rebuild ran every stale handler, so every switch
     took longer than the one before. Measured on the preview theme, headless Edge, 30 switches:
     Appstle rebuild 83 ms -> 200 ms without the guard, flat at ~45 ms with it.
     The guard keeps the newest copy of each: an Appstle <style> whose text repeats one already in
     <head> replaces the older copy, and after each Appstle render any document handler delegated
     to an Appstle selector that repeats a newer one (same selector, namespace and source) is
     dropped through jQuery's own handler list. Nothing outside Appstle is touched. */
  function armAppstleGuards() {
    if (window.__pcbAppstleGuard) return;
    const guard = (window.__pcbAppstleGuard = { stylesDropped: 0, handlersDropped: 0 });
    /* Appstle's styles are bare <style> tags naming its own selectors; the empty custom-CSS tag it
       appends right after them is taken as Appstle's too (its previous sibling says so) */
    const bare = (n) => n && n.nodeType === 1 && n.tagName === 'STYLE' && n.attributes.length === 0;
    const isAppstleStyle = (n) => bare(n) && (/appstle/i.test(n.textContent || '') || ((n.textContent || '').trim() === '' && bare(n.previousElementSibling) && /appstle/i.test(n.previousElementSibling.textContent || '')));
    const styleKey = (n) => (n.textContent || '').trim();
    const styles = new Map();
    document.head.querySelectorAll('style').forEach((n) => { if (isAppstleStyle(n)) styles.set(styleKey(n), n); });
    new MutationObserver((muts) => {
      muts.forEach((m) => m.addedNodes.forEach((n) => {
        if (!isAppstleStyle(n)) return;
        const k = styleKey(n);
        const prev = styles.get(k);
        if (prev && prev !== n && prev.isConnected) { prev.remove(); guard.stylesDropped++; }
        styles.set(k, n);
      }));
    }).observe(document.head, { childList: true });

    const prune = () => {
      const libs = [];
      ['appstle_jQuery', 'jQuery', '$'].forEach((name) => {
        try {
          const j = window[name];
          if (j && j.fn && j.fn.jquery && typeof j._data === 'function' && libs.indexOf(j) < 0) libs.push(j);
        } catch (e) { /* a getter that throws: not a jQuery */ }
      });
      libs.forEach((j) => {
        const events = j._data(document, 'events');
        if (!events) return;
        Object.keys(events).forEach((type) => {
          const list = events[type];
          if (!Array.isArray(list) || list.length < 2) return;
          const kept = {};
          for (let i = list.length - 1; i >= 0; i--) {
            const h = list[i];
            if (!h || !h.selector || !/appstle/i.test(h.selector)) continue;
            const sig = h.selector + '|' + (h.namespace || '') + '|' + String(h.handler);
            if (kept[sig]) { list.splice(i, 1); guard.handlersDropped++; } else kept[sig] = true;
          }
          /* jQuery keeps the delegated handlers first and counts them here */
          list.delegateCount = list.filter((h) => h && h.selector).length;
        });
      });
    };
    let timer;
    const schedule = () => { clearTimeout(timer); timer = setTimeout(prune, 150); };
    document.addEventListener('AppstleSubscription:SubscriptionWidget:sellingPlanChanged', schedule);
    document.addEventListener('appstle:subscription-widget:loaded', schedule);
    document.addEventListener('product:variant-change', schedule);
    schedule();
  }

  /* ---- wishlist toast ----
     Wishlist King fires wk:wishlist:add:success / wk:wishlist:remove:success on document once a
     heart has been tapped and the change is saved. A small pill drops in from the top of the
     screen saying which, with a link to the wishlist page, and leaves after a few seconds (it
     waits while the pointer is over it). It is appended to <body>, not the section, so the sticky
     header's stacking and the main content's clip-path cannot cut it off. One per page. */
  function armWishlistToast(url) {
    if (window.__pcbWishToast) return;
    const heart = '<svg viewBox="0 0 64 64" aria-hidden="true"><path d="M32.012,59.616c-1.119-.521-2.365-1.141-3.707-1.859a79.264,79.264,0,0,1-11.694-7.614C6.316,42,.266,32.6.254,22.076,0.244,12.358,7.871,4.506,17.232,4.5a16.661,16.661,0,0,1,11.891,4.99l2.837,2.889,2.827-2.9a16.639,16.639,0,0,1,11.874-5.02h0c9.368-.01,17.008,7.815,17.021,17.539,0.015,10.533-6.022,19.96-16.312,28.128a79.314,79.314,0,0,1-11.661,7.63C34.369,58.472,33.127,59.094,32.012,59.616Z"/></svg>';
    const cross = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
    const toast = document.createElement('div');
    toast.className = 'pcb-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.hidden = true;
    toast.innerHTML = '<span class="pcb-toast__icon">' + heart + '</span><span class="pcb-toast__text" data-toast-text></span><a class="pcb-toast__link" href="' + url + '">View wishlist</a><button type="button" class="pcb-toast__close" aria-label="Dismiss">' + cross + '</button>';
    document.body.appendChild(toast);
    const text = toast.querySelector('[data-toast-text]');
    let hideTimer;
    let unhideTimer;
    const hide = () => {
      clearTimeout(hideTimer);
      toast.classList.remove('is-open');
      clearTimeout(unhideTimer);
      unhideTimer = setTimeout(() => { toast.hidden = true; }, 350);
    };
    const show = (message, removed) => {
      clearTimeout(hideTimer);
      clearTimeout(unhideTimer);
      text.textContent = message;
      toast.classList.toggle('is-removed', !!removed);
      toast.hidden = false;
      /* two frames on, so the slide runs from the hidden position even when it was display: none */
      requestAnimationFrame(() => requestAnimationFrame(() => toast.classList.add('is-open')));
      hideTimer = setTimeout(hide, 4500);
    };
    toast.querySelector('.pcb-toast__close').addEventListener('click', hide);
    toast.addEventListener('mouseenter', () => clearTimeout(hideTimer));
    toast.addEventListener('mouseleave', () => { clearTimeout(hideTimer); hideTimer = setTimeout(hide, 2000); });
    document.addEventListener('wk:wishlist:add:success', () => show('Added to your wishlist', false));
    document.addEventListener('wk:wishlist:remove:success', () => show('Removed from your wishlist', true));
    window.__pcbWishToast = toast;
  }

  class CleanBuybox extends HTMLElement {
    connectedCallback() {
      armAppstleGuards();
      armWishlistToast(this.dataset.wishlistUrl || (((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'pages/wishlist'));
      const dataEl = this.querySelector('[data-pcb-data]');
      try {
        this.data = JSON.parse(dataEl ? dataEl.textContent : '{}');
      } catch (e) {
        console.warn('[clean-buybox] product data did not parse', e);
        this.data = { variants: [], groups: [] };
      }
      /* Shopify's variant JSON: selling_plan_allocations[] -> { selling_plan_id, price } */
      this.variants = (this.data.variants || []).map((v) => {
        const plans = {};
        (v.selling_plan_allocations || []).forEach((a) => { plans[String(a.selling_plan_id)] = a.price; });
        return {
          id: v.id,
          title: v.title,
          options: [v.option1, v.option2, v.option3].filter((o) => o !== null && o !== undefined),
          available: v.available,
          price: v.price,
          compare_at_price: v.compare_at_price || 0,
          plans: plans
        };
      });
      this.plans = {};
      (this.data.groups || []).forEach((g) => {
        (g.selling_plans || []).forEach((p) => { this.plans[String(p.id)] = { group: String(g.id), name: p.name }; });
      });
      this.variantSelects = this.querySelector('variant-selects');
      this.planInput = this.querySelector('[data-selling-plan-input]');
      this.planGroupRadios = Array.from(this.querySelectorAll('input[data-plan-group]'));
      this.appPlans = this.querySelector('[data-app-plans]');
      this.freqBoxes = Array.from(this.querySelectorAll('[data-plan-freq]'));
      this.summary = this.querySelector('[data-summary]');
      this.priceNow = this.querySelector('[data-price-now]');
      this.priceWas = this.querySelector('[data-price-was]');
      this.sub = this.querySelector('[data-variant-sub]');
      this.perCup = this.querySelector('[data-per-cup]');
      this.qtyInput = this.querySelector('[data-qty-input]');
      this.perCupLabel = this.dataset.perCupLabel || 'per cup';
      this.onceLabel = this.dataset.onceLabel || '';

      try {
        this.money = new Intl.NumberFormat(this.dataset.locale || undefined, { style: 'currency', currency: this.dataset.currency || 'USD' });
      } catch (e) {
        this.money = null;
      }

      if (this.variantSelects) {
        this.variantSelects.addEventListener('change', () => this.update());
        /* four or more sizes scroll sideways: a scroll indicator under the row, edge fades while
           there is more that way, and the chosen tile brought into view on arrival */
        this.variantSelects.querySelectorAll('fieldset').forEach((fs) => this.setupSizeScroller(fs));
      }
      this.planGroupRadios.forEach((r) => r.addEventListener('change', () => this.onPlanChange()));
      this.freqBoxes.forEach((box) => {
        const sel = box.querySelector('select');
        if (sel) sel.addEventListener('change', () => this.onPlanChange());
      });
      document.addEventListener('product:variant-change', (e) => {
        if (e.detail && String(e.detail.sectionId) === String(this.dataset.section)) this.update();
      });
      if (this.qtyInput) {
        /* the +/- buttons dispatch change; typing dispatches input */
        this.qtyInput.addEventListener('change', () => this.update());
        this.qtyInput.addEventListener('input', () => this.update());
      }
      if (this.appPlans) {
        /* Appstle fires this with the selected plan id ('' for one-time) on every change and re-render */
        document.addEventListener('AppstleSubscription:SubscriptionWidget:sellingPlanChanged', () => this.update());
        document.addEventListener('appstle:subscription-widget:loaded', () => this.update());
        /* any other app: radios and the frequency select bubble change events up to here */
        this.appPlans.addEventListener('change', () => this.update());
        /* a click anywhere on an option row picks it (the CSS overlay handles most of this; this
           covers the row padding and anything the overlay misses). Labels, selects and links
           already do their own thing, so they are left alone. */
        this.appPlans.addEventListener('click', (e) => {
          if (e.target.closest('label, select, option, a, button, input')) return;
          const row = e.target.closest('.appstle_subscription_wrapper_option');
          const radio = row && row.querySelector('input[name="selling_plan"]');
          if (radio && !radio.checked) radio.click();
        });
      }

      const share = this.querySelector('[data-share]');
      if (share) share.addEventListener('click', () => this.share(share));

      /* the Frequently Bought Together block: open it when picked in the theme editor, so the
         merchant sees what they are editing */
      this.fbt = this.querySelector('[data-fbt]');
      this.setupFbtToggle();
      if (this.fbt && window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', (e) => {
          if (e.target && (e.target === this.fbt || this.fbt.contains(e.target))) this.fbt.open = true;
        });
      }
      this.setupFbtThumbs();
      this.setupFbtBridge();
      this.setupFbtModal();
      this.setupGalleryResize();

      this.onPlanChange(true);
    }

    /* ---- the gallery follows late layout changes ----
       Flickity (product.js) sizes its viewport once at init and again only on window resize. When
       the gallery column changes width after that without the window changing (a stylesheet that
       arrives late, fonts, a scrollbar appearing), the viewport keeps the old height and clips the
       image until something fires resize, which is what the cart drawer opening does. This asks
       Flickity to re-measure whenever the slider's own width changes. */
    setupGalleryResize() {
      const detail = this.closest('.thb-product-detail');
      const slider = detail && detail.querySelector('product-slider');
      if (!slider || !('ResizeObserver' in window)) return;
      let lastWidth = slider.getBoundingClientRect().width;
      let frame;
      new ResizeObserver((entries) => {
        const width = entries[0] ? entries[0].contentRect.width : slider.getBoundingClientRect().width;
        if (Math.abs(width - lastWidth) < 1) return;
        lastWidth = width;
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          if (slider.flkty && typeof slider.flkty.resize === 'function') slider.flkty.resize();
        });
      }).observe(slider);
    }

    /* ---- Frequently Bought Together v2: the bundle is the app's own thing ----
       The widget (data-fbt-api="v2", block setting "Use the theme's Add to cart button" on) lists
       every row of the bundle, this product among them at the app's own size, and dispatches
       fbt:selection { items, count, total } (total in dollars) whenever the ticks change. The
       theme only mirrors that in the bundle card and the modal, and the modal's button hands the
       add back to the widget (window.FBT.addSelected(), attribution included). The page's own
       price, Add to cart and Buy with Shop know nothing about the bundle. */
    setupFbtBridge() {
      const root = this.querySelector('#fbt-widget-root');
      if (!root || root.dataset.fbtApi !== 'v2' || root.dataset.externalButton !== 'true') return;
      this.fbtSelection = { items: [], count: 0, total: 0 };
      root.addEventListener('fbt:selection', (e) => {
        const d = e.detail || {};
        this.fbtSelection = { items: d.items || [], count: d.count || 0, total: Number(d.total) || 0 };
        if (this.renderFbtStrip) this.renderFbtStrip();
      });
    }

    /* ---- the sideways size row ---- */
    setupSizeScroller(fs) {
      const arm = () => {
        if (fs.dataset.pcbScroller || fs.scrollWidth <= fs.clientWidth + 1) return;
        fs.dataset.pcbScroller = '1';
        fs.classList.add('pcb__seg--scroll');
        /* dots under the row, one per group of tiles that fit in view (three on most screens);
           the active dot follows the scrolling and a tap on a dot slides to that group */
        const dots = document.createElement('div');
        dots.className = 'pcb__seg-dots';
        dots.setAttribute('role', 'group');
        dots.setAttribute('aria-label', 'More sizes');
        fs.insertAdjacentElement('afterend', dots);
        const GAP = 8;
        const geometry = () => {
          const tile = fs.querySelector('input[type="radio"] + label');
          const count = fs.querySelectorAll('input[type="radio"] + label').length;
          const tileW = tile ? tile.offsetWidth : fs.clientWidth;
          const per = Math.max(1, Math.floor((fs.clientWidth + GAP) / (tileW + GAP)));
          return { per, pages: Math.max(1, Math.ceil(count / per)), pageW: per * (tileW + GAP) };
        };
        const update = () => {
          const range = fs.scrollWidth - fs.clientWidth;
          const g = geometry();
          /* each dot's stop is its group's start, the last one capped at the row's end (which is
             usually less than a full page along), so the active dot is the nearest stop */
          let current = 0;
          let best = Infinity;
          for (let i = 0; i < g.pages; i++) {
            const stop = Math.min(range, i * g.pageW);
            const d = Math.abs(fs.scrollLeft - stop);
            if (d < best) { best = d; current = i; }
          }
          while (dots.children.length < g.pages) {
            const d = document.createElement('button');
            d.type = 'button';
            d.className = 'pcb__seg-dot';
            const i = dots.children.length;
            d.setAttribute('aria-label', 'Sizes, page ' + (i + 1));
            d.addEventListener('click', () => fs.scrollTo({ left: Math.min(range, i * geometry().pageW), behavior: 'smooth' }));
            dots.appendChild(d);
          }
          while (dots.children.length > g.pages) dots.lastElementChild.remove();
          Array.from(dots.children).forEach((d, i) => d.classList.toggle('is-active', i === current));
          dots.hidden = g.pages < 2;
          fs.classList.toggle('is-start', fs.scrollLeft < 2);
          fs.classList.toggle('is-end', fs.scrollLeft > range - 2);
        };
        fs.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        const on = fs.querySelector('input:checked + label');
        /* sideways only (scrollIntoView could also move the page on arrival) */
        if (on) fs.scrollLeft = Math.max(0, on.offsetLeft - (fs.clientWidth - on.offsetWidth) / 2);
        update();
      };
      arm();
      /* fonts and layout can settle after connect; look again once they have */
      window.addEventListener('load', arm);
    }

    /* ---- selection ---- */
    selectedOptions() {
      if (!this.variantSelects) return null;
      return Array.from(this.variantSelects.querySelectorAll('fieldset')).map((fs) => {
        const sel = fs.querySelector('select');
        if (sel) return sel.value;
        const on = fs.querySelector('input:checked');
        return on ? on.value : null;
      });
    }

    currentVariant() {
      if (!this.variants.length) return null;
      const opts = this.selectedOptions();
      if (!opts) return this.variants[0];
      return this.variants.find((v) => v.options.every((o, i) => o === opts[i])) || null;
    }

    currentPlanId() {
      if (this.appPlans) return this.appPlanId();
      const on = this.planGroupRadios.find((r) => r.checked);
      if (!on || !on.value) return '';
      const box = this.freqBoxes.find((b) => b.dataset.planFreq === on.value);
      const sel = box && box.querySelector('select');
      return sel ? sel.value : '';
    }

    /* Read the plan the app widget has selected. Appstle keeps the subscribe radio's value empty
       until it is clicked (the id sits in data-appstle-real-value) and puts the frequency in a
       <select> inside the same option row, so the select wins when there is one. The widget is
       re-rendered on variant change, so this queries the DOM each time instead of caching nodes. */
    appPlanId() {
      const on = this.appPlans.querySelector('input[name="selling_plan"]:checked');
      if (!on) return '';
      const row = on.closest('.appstle_subscription_wrapper_option') || on.parentElement;
      const sel = row && row.querySelector('select');
      let id = on.value || on.dataset.appstleRealValue || '';
      if (sel && sel.value && (on.value || on.dataset.appstleRealValue)) id = sel.value;
      return /^\d+$/.test(id) ? id : '';
    }

    onPlanChange(silent) {
      const on = this.planGroupRadios.find((r) => r.checked);
      const group = on ? on.value : '';
      this.freqBoxes.forEach((box) => {
        const show = group && box.dataset.planFreq === group;
        box.hidden = !show;
        /* a single plan needs no dropdown */
        const sel = box.querySelector('select');
        if (show && sel && sel.options.length < 2) box.hidden = true;
      });
      if (this.planInput) {
        const id = this.currentPlanId();
        this.planInput.value = id;
        /* a disabled field is left out of the form, so "Just once" sends no selling_plan at all */
        this.planInput.disabled = !id;
      }
      this.update();
    }

    /* ---- display ---- */
    format(cents) {
      if (this.money) return this.money.format(cents / 100);
      return '$' + (cents / 100).toFixed(2);
    }

    servingsFrom(title) {
      const m = /(\d+)(?:\s*[-–]\s*(\d+))?\s*(?:servings?|cups?)/i.exec(title || '');
      if (!m) return 0;
      const a = parseInt(m[1], 10);
      const b = m[2] ? parseInt(m[2], 10) : a;
      return Math.max(a, b);
    }

    update() {
      const v = this.currentVariant();
      if (!v) return;
      const planId = this.currentPlanId();
      const planPrice = planId && v.plans && v.plans[planId] != null ? v.plans[planId] : null;
      const price = planPrice != null ? planPrice : v.price;
      /* the summary is this product's line: unit price times the How many? quantity */
      const qty = this.qtyInput ? Math.max(1, parseInt(this.qtyInput.value, 10) || 1) : 1;

      if (this.priceNow) this.priceNow.textContent = this.format(price * qty);
      if (this.priceWas) {
        const was = planPrice != null && planPrice < v.price ? v.price : (v.compare_at_price > v.price ? v.compare_at_price : 0);
        this.priceWas.hidden = !was;
        this.priceWas.textContent = was ? this.format(was * qty) : '';
      }

      const parts = (v.title || '').split(' (');
      const subText = parts[1] ? parts[1].replace(/\)$/, '') : '';
      if (this.sub) this.sub.textContent = subText;
      if (this.summary) this.summary.textContent = 'Total price';
      if (this.perCup) {
        const servings = this.servingsFrom(v.title);
        this.perCup.textContent = servings ? this.format(Math.round(price / servings)) + ' ' + this.perCupLabel : '';
      }
    }
    /* ---- Frequently Bought Together row: eased open and close ----
       <details> snaps. The summary click is taken over: opening sets open (so the panel has a
       height to measure) and grows the panel from 0; closing shrinks it and then removes open.
       .is-open drives the chevron so it turns as the motion starts, not when open lands.
       Reduced-motion users get the native toggle. */
    setupFbtToggle() {
      const d = this.fbt;
      const summary = d && d.querySelector('summary');
      const panel = d && d.querySelector('.pcb__fbt-panel');
      if (!d || !summary || !panel) return;
      d.classList.toggle('is-open', d.open);
      const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      /* a quick second or third click is a double-click to the browser, which selects the nearest
         text (the panel's). Cancel the multi-click before selection starts. */
      summary.addEventListener('mousedown', (e) => {
        if (e.detail > 1) e.preventDefault();
      });
      summary.addEventListener('click', (e) => {
        if (reduced || !panel.animate) { d.classList.toggle('is-open', !d.open); return; }
        e.preventDefault();
        if (d.classList.contains('is-animating')) return;
        d.classList.add('is-animating');
        const timing = { duration: 280, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' };
        /* the panel is border-box, so scrollHeight (content plus padding) is the open height; the
           padding and the negative bottom margin (room for the rows' shadows, see the CSS)
           animate with it so nothing is left showing at 0 and the card does not jump */
        const cs = getComputedStyle(panel);
        const shut = { height: '0px', paddingTop: '0px', paddingBottom: '0px', marginBottom: '0px', opacity: 0 };
        const openTo = (h) => ({ height: h + 'px', paddingTop: cs.paddingTop, paddingBottom: cs.paddingBottom, marginBottom: cs.marginBottom, opacity: 1 });
        if (!d.open) {
          d.open = true;
          d.classList.add('is-open');
          const a = panel.animate([shut, openTo(panel.scrollHeight)], timing);
          a.onfinish = () => d.classList.remove('is-animating');
        } else {
          d.classList.remove('is-open');
          const a = panel.animate([openTo(panel.scrollHeight), shut], timing);
          a.onfinish = () => { d.open = false; d.classList.remove('is-animating'); };
        }
      });
    }

    /* ---- Frequently Bought Together: a photo beside each row ----
       The FBT app renders its product photos only in the trio at the top (.fbt-images-row) and its
       rows without one. This reads each photo's URL from the trio (matched to a row by the product
       title, which is the img alt) and hands it to the row as a CSS variable; product-clean.css
       paints it with a ::before and hides the trio once every row has one. Only a style attribute
       on the row and a class on the app's mount node are touched, never the React-managed tree, so
       the app's re-renders (checkbox toggles, size changes) are safe; an observer re-applies after
       them. Photos are cached by title because the trio only shows checked items. */
    setupFbtThumbs() {
      const root = this.querySelector('#fbt-widget-root');
      if (!root) return;
      /* the v2 app draws its own row layout (photos, whole-row ticking, no current product), so
         none of the retrofits below apply to it */
      if (root.dataset.fbtApi === 'v2') return;
      const thumbs = {};
      const apply = () => {
        root.querySelectorAll('.fbt-images-row .fbt-image').forEach((img) => {
          const src = img.currentSrc || img.src;
          if (img.alt && src) thumbs[img.alt.trim()] = src;
        });
        const rows = root.querySelectorAll('.fbt-row');
        let all = rows.length > 0;
        /* the product being viewed: the app lists it first, ticked. With data-fbt-self set, that
           row is unticked through the app's own checkbox (so its total and Add to cart drop it)
           and hidden. Rows are re-created on the app's re-renders, so each new row element is
           handled once. */
        const self = (this.dataset.fbtSelf || '').trim();
        rows.forEach((row) => {
          const title = row.querySelector('.fbt-title');
          if (self && title && title.textContent.trim() === self) {
            row.classList.add('is-self');
            if (!row.dataset.pcbSelfDone) {
              row.dataset.pcbSelfDone = '1';
              const box = row.querySelector('input[type="checkbox"]');
              if (box && box.checked) box.click();
            }
          }
          const src = title && thumbs[title.textContent.trim()];
          if (src) {
            row.style.setProperty('--fbt-thumb', 'url("' + src + '")');
            row.classList.add('has-thumb');
          } else {
            all = false;
          }
        });
        root.classList.toggle('pcb-has-thumbs', all);
        /* classic widget: the badge counts the ticked rows other than this product */
        let ticked = 0;
        rows.forEach((row) => {
          const box = row.querySelector('input[type="checkbox"]');
          if (box && box.checked && !row.classList.contains('is-self')) ticked++;
        });
        this.setFbtBadge(ticked);
      };
      apply();
      let timer;
      new MutationObserver(() => {
        clearTimeout(timer);
        timer = setTimeout(apply, 60);
      }).observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset', 'class'] });
      /* the trio's photos may still be loading when the widget first renders */
      root.addEventListener('load', () => apply(), true);

      /* the whole row toggles its checkbox, except the size select (and the checkbox and title,
         which the app already wires up itself) */
      root.addEventListener('click', (e) => {
        if (e.target.closest('select, option, input, .fbt-title, a, button')) return;
        const row = e.target.closest('.fbt-row');
        const box = row && row.querySelector('input[type="checkbox"]');
        if (box) box.click();
      });
    }

    /* the cart drawer, re-rendered from the server (the way its own refresh() does) and opened;
       used after the widget did the adding */
    openCartDrawerFresh() {
      const drawer = document.getElementById('Cart-Drawer');
      if (!drawer) return;
      const open = () => { if (typeof drawer.open === 'function') drawer.open(); };
      if (typeof drawer.getSectionsToRender !== 'function' || typeof drawer.getSectionInnerHTML !== 'function') { open(); return; }
      const sections = drawer.getSectionsToRender();
      fetch(window.location.pathname + '?sections=' + sections.map((x) => x.section).join(','))
        .then((r) => r.json())
        .then((state) => {
          sections.forEach((section) => {
            const el = document.getElementById(section.id);
            if (!el || !state[section.section]) return;
            (el.querySelector(section.selector) || el).innerHTML = drawer.getSectionInnerHTML(state[section.section], section.selector);
          });
        })
        .catch(() => {})
        .then(open);
    }

    /* a Shopify CDN image URL resized and centre-cropped to w x h pixels (the CDN's own width,
       height and crop parameters); any other URL comes back as it is */
    cdnImage(url, w, h) {
      try {
        const u = new URL(url, window.location.href);
        const shopify = /(^|\.)cdn\.shopify\.com$/.test(u.hostname) || u.pathname.indexOf('/cdn/shop/') === 0;
        if (!shopify) return url;
        u.searchParams.set('width', String(Math.round(w)));
        u.searchParams.set('height', String(Math.round(h)));
        u.searchParams.set('crop', 'center');
        return u.toString();
      } catch (e) {
        return url;
      }
    }

    /* true while the bundle modal is open: ticks made in it show in the modal only until it
       closes (which ticks everything again), so the card behind it holds still */
    fbtModalOpen() {
      const modal = this.querySelector('[data-fbt-modal]');
      return !!(modal && modal.open);
    }

    /* ---- Frequently Bought Together: the bundle card and the modal ----
       The app's widget renders once, hidden, in .pcb__fbt-panel. The card mirrors it: a fanned stack
       of the rows' photos (from each row's img, dimmed while unticked), how many are ticked, their names and the
       app's total. The card button moves the panel into the modal; closing moves it back and ticks
       every row again. The modal's Add to cart hands the ticked rows to the widget, then opens
       the cart drawer. */
    setupFbtModal() {
      const wrap = this.fbt;
      const modal = this.querySelector('[data-fbt-modal]');
      if (!wrap || !modal) return;
      const trio = wrap.querySelector('[data-fbt-trio]');
      const includes = wrap.querySelector('[data-fbt-includes]');
      const openBtn = wrap.querySelector('[data-fbt-open]');
      const panel = wrap.querySelector('[data-fbt-panel]');
      const root = panel && panel.querySelector('#fbt-widget-root');
      const body = modal.querySelector('[data-fbt-modal-body]');
      const total = modal.querySelector('[data-fbt-modal-total]');
      const addBtn = modal.querySelector('[data-fbt-modal-add]');
      const countEl = wrap.querySelector('[data-fbt-bundle-count]');
      const totalEl = wrap.querySelector('[data-fbt-bundle-total]');
      if (!trio || !openBtn || !panel || !body) return;

      const render = () => {
        const rows = root ? Array.from(root.querySelectorAll('.fbt-item, .fbt-row')).filter((r) => !r.classList.contains('fbt-item--skeleton')) : [];
        const frozen = this.fbtModalOpen();
        /* while the modal is open the strip is rebuilt into a scratch node, so the card keeps
           showing what it showed when the modal opened */
        const trioOut = frozen ? document.createElement('span') : trio;
        trioOut.querySelectorAll('[data-fbt-trio-companion]').forEach((n) => n.remove());
        const names = [];
        rows.forEach((row, index) => {
          const img = row.querySelector('img');
          const nameEl = row.querySelector('.fbt-item-name, .fbt-title');
          const name = nameEl ? nameEl.textContent.trim() : '';
          const box = row.querySelector('input[type="checkbox"]');
          const on = !!(box && box.checked);
          if (on && name) names.push(name);
          const item = document.createElement('span');
          item.className = 'pcb__fbt-trio-item' + (on ? ' is-on' : '');
          item.dataset.fbtTrioCompanion = '1';
          if (name) item.title = name;
          /* the app's newer rows carry their photo's URL on the row (data-fbt-photo); older ones
             have an img in the row */
          const src = row.dataset.fbtPhoto || (img && (img.currentSrc || img.src));
          if (src) {
            /* the row's img is a small thumbnail; the stack asks the CDN for its own size (the front
               photo is 72x84, the others 64x74), at 1x, 2x and 3x, cropped to the same shape */
            const w = index === 0 ? 72 : 64;
            const h = index === 0 ? 84 : 74;
            const i = document.createElement('img');
            i.src = this.cdnImage(src, w * 2, h * 2);
            i.srcset = [1, 2, 3].map((n) => this.cdnImage(src, w * n, h * n) + ' ' + n + 'x').join(', ');
            i.width = w;
            i.height = h;
            i.alt = '';
            i.loading = 'lazy';
            i.decoding = 'async';
            item.appendChild(i);
          }
          trioOut.appendChild(item);
        });
        /* until the app has its products, the placeholder shows instead of a one-photo strip; a
           bundle needs at least two rows, otherwise the block steps aside */
        const widgetEl = root && root.querySelector('.fbt-widget');
        const loading = !!root && (!widgetEl || widgetEl.classList.contains('is-loading'));
        const enough = rows.length >= 2;
        wrap.classList.toggle('is-loading', loading);
        wrap.hidden = !loading && !enough;
        trio.hidden = !enough;
        const n = names.length;
        const money = this.format(this.fbtSelection ? Math.round((this.fbtSelection.total || 0) * 100) : 0);
        if (!frozen) {
          if (includes) {
            includes.textContent = '';
            if (n) {
              const lead = document.createElement('b');
              lead.textContent = 'Includes: ';
              includes.appendChild(lead);
              includes.appendChild(document.createTextNode(names.join(', ')));
            } else if (enough) {
              includes.textContent = 'Nothing selected. Pick what to include.';
            }
          }
          if (countEl) countEl.textContent = !enough ? '' : n > 1 ? 'Bundle of ' + n + ' items' : n === 1 ? 'Just one item' : 'Nothing selected';
          if (totalEl) totalEl.textContent = money;
        }
        /* the modal's own total and button are always live */
        if (total) total.textContent = money;
        if (addBtn && !addBtn.classList.contains('loading')) {
          addBtn.textContent = n ? 'Add to cart' : 'Nothing selected';
          addBtn.disabled = !n;
        }
      };

      if (root) {
        let timer;
        new MutationObserver(() => { clearTimeout(timer); timer = setTimeout(render, 60); }).observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'class', 'checked'] });
        root.addEventListener('change', () => setTimeout(render, 0));
        root.addEventListener('load', () => render(), true);
      }
      render();
      this.renderFbtStrip = render;

      /* on phones the stack fans open once the card is nearly all in frame (the CSS reads
         .is-in-view under its small-screen media query) and folds back once most of it has left;
         the gap between the two keeps it from flickering at the edge */
      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio >= 0.9) openBtn.classList.add('is-in-view');
            else if (entry.intersectionRatio < 0.5) openBtn.classList.remove('is-in-view');
          });
        }, { threshold: [0.5, 0.9] }).observe(openBtn);
      }

      /* the modal's own foot only makes sense when the app block hands the adding to the theme
         ("Use the theme's Add to cart button"); otherwise the widget draws its own total and
         button and ours would double up */
      const foot = modal.querySelector('.pcb-modal__foot');
      if (foot) foot.hidden = !(root && root.dataset.externalButton === 'true');

      /* the back button (a phone's especially) closes the modal instead of leaving the page:
         opening pushes one history entry, going back pops it and closes; closing any other way
         pops the entry itself so back never lands on a stale stop */
      const HIST = 'pcbBundleModal';
      window.addEventListener('popstate', () => {
        if (!modal.open) return;
        this.pcbHistPopped = true;
        close();
      });
      const open = () => {
        panel.hidden = false;
        body.appendChild(panel);
        render();
        if (typeof modal.showModal === 'function') modal.showModal();
        else modal.setAttribute('open', '');
        try { window.history.pushState(Object.assign({}, window.history.state || {}, { [HIST]: true }), ''); } catch (e) { /* history unavailable: back just leaves */ }
        /* the page behind holds still without being touched: the dialog fills the viewport as a
           scroll container of its own (product-clean.css), so wheel and touch over the backdrop
           have nowhere to go and the page keeps its scrollbar and position */
        /* the page's own scrolling is switched off too (product-clean.css); the width of the
           scrollbar that disappears is handed to the CSS so the page does not shift */
        const sbw = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
        document.documentElement.style.setProperty('--pcb-sbw', sbw + 'px');
        document.documentElement.classList.add('pcb-modal-open');
        window.dispatchEvent(new Event('resize'));
      };
      const close = () => {
        if (modal.open) modal.close();
        else if (modal.hasAttribute('open')) modal.dispatchEvent(new Event('close'));
      };
      modal.addEventListener('close', () => {
        panel.hidden = true;
        wrap.appendChild(panel);
        document.documentElement.classList.remove('pcb-modal-open');
        document.documentElement.style.removeProperty('--pcb-sbw');
        /* closed by back: the entry is already gone; closed any other way: take it off now */
        if (this.pcbHistPopped) this.pcbHistPopped = false;
        else if (window.history.state && window.history.state[HIST]) window.history.back();
        if (addBtn) addBtn.classList.remove('loading');
        /* closing (X, backdrop, Escape, or after adding) puts the bundle back to everything ticked,
           through the app's own checkboxes */
        if (root) {
          root.querySelectorAll('.fbt-item:not(.fbt-item--skeleton) input[type="checkbox"], .fbt-row input[type="checkbox"]').forEach((box) => {
            if (!box.checked) box.click();
          });
        }
        render();
      });
      openBtn.addEventListener('click', open);
      modal.querySelectorAll('[data-fbt-close]').forEach((b) => b.addEventListener('click', close));
      /* a click on the backdrop (the dialog itself, outside the box) closes */
      modal.addEventListener('click', (e) => { if (e.target === modal) close(); });

      /* the modal's Add to cart: the widget adds what is ticked (its cart attribute and analytics
         event included), then the drawer opens on the result and the modal closes */
      if (addBtn) {
        addBtn.addEventListener('click', () => {
          const api = window.FBT;
          if (!api || typeof api.addSelected !== 'function' || addBtn.classList.contains('loading')) return;
          addBtn.classList.add('loading');
          addBtn.disabled = true;
          const done = (ok, why) => {
            addBtn.classList.remove('loading');
            addBtn.disabled = false;
            if (ok) {
              this.openCartDrawerFresh();
              close();
            } else {
              console.warn('[clean-buybox] the Frequently Bought Together add did not go through', why);
            }
          };
          api.addSelected({ source: 'theme' }).then((r) => done(!!(r && r.ok), r && r.error), (err) => done(false, err));
        });
      }
    }
    /* ---- the count badge on the folded Frequently Bought Together row's icon ----
       Shows how many blends are ticked so a closed row still says something is in there;
       product-clean.css hides it while the row is open. */
    setFbtBadge(count) {
      const badge = this.querySelector('[data-fbt-badge]');
      if (!badge) return;
      const n = Number(count) || 0;
      badge.textContent = n > 9 ? '9+' : String(n);
      badge.hidden = n === 0;
      if (this.fbt) this.fbt.classList.toggle('has-picks', n > 0);
    }

    /* ---- share ---- */
    /* the share link opens the share sheet (the classic template's networks in a small modal);
       where the section has none, it falls back to copying the link */
    share(btn) {
      const modal = this.querySelector('[data-share-modal]');
      if (modal && typeof modal.showModal === 'function') {
        if (!modal.dataset.pcbWired) {
          modal.dataset.pcbWired = '1';
          const close = () => { if (modal.open) modal.close(); };
          modal.querySelectorAll('[data-share-close]').forEach((b) => b.addEventListener('click', close));
          modal.addEventListener('click', (e) => { if (e.target === modal) close(); });
          modal.addEventListener('close', () => {
            document.documentElement.classList.remove('pcb-modal-open');
            document.documentElement.style.removeProperty('--pcb-sbw');
          });
          const sheet = modal.querySelector('.share-article');
          const touch = navigator.maxTouchPoints > 0 || window.matchMedia('(pointer: coarse)').matches;
          if (sheet && touch) {
            /* the theme's social-share script opens each network in a small pop-up window, which
               phone browsers block without a word; on touch devices the links open as ordinary
               new tabs instead (its handler is cut off before it runs) */
            sheet.querySelectorAll('a.social').forEach((a) => {
              a.target = '_blank';
              a.rel = 'noopener';
              a.addEventListener('click', (e) => e.stopImmediatePropagation(), true);
            });
          }
          if (sheet && navigator.share) {
            /* the device's own share sheet, where there is one */
            const more = document.createElement('button');
            more.type = 'button';
            more.className = 'social pcb-share__more';
            more.title = 'More';
            more.innerHTML = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4"/></svg>';
            more.addEventListener('click', () => {
              navigator.share({ title: btn.dataset.title, url: btn.dataset.url }).catch(() => {});
            });
            sheet.appendChild(more);
          }
        }
        const sbw = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
        document.documentElement.style.setProperty('--pcb-sbw', sbw + 'px');
        document.documentElement.classList.add('pcb-modal-open');
        modal.showModal();
        return;
      }
      const payload = { title: btn.dataset.title, url: btn.dataset.url };
      const toast = btn.querySelector('.pcb__share-toast');
      const say = (msg) => {
        if (!toast) return;
        toast.textContent = msg;
        btn.classList.add('is-toast');
        clearTimeout(this._toastTimer);
        this._toastTimer = setTimeout(() => { btn.classList.remove('is-toast'); toast.textContent = ''; }, 1800);
      };
      if (navigator.share) {
        navigator.share(payload).catch(() => {});
        return;
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(payload.url).then(() => say('Link copied'), () => say(payload.url));
      } else {
        say(payload.url);
      }
    }
  }

  customElements.define('clean-buybox', CleanBuybox);
})();
